import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { LuDownload } from 'react-icons/lu';
import Loading from '../../shared/components/Loading/Loading';
import SidePanel from '../../shared/components/SidePanel/SidePanel';
import { useReviews } from './hooks/useReviews';
import { useReviewFilters } from './hooks/useReviewFilters';
import { exportReviewsToXls } from './services/exportReviews';
import ProductSelect from './components/ProductSelect';
import ReviewsStatsCards from './components/ReviewsStatsCards';
import ReviewsTable from './components/ReviewsTable';
import ReviewFiltersPanel from './components/ReviewFiltersPanel';
import ReviewEditPanel from './components/ReviewEditPanel';
import '../../component/admin/Product/Create.css';
import './Reviews.css';

const ReviewsPage = () => {
  const { reviews, products, loading, error, saveReview, removeReview } = useReviews();
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [saving, setSaving] = useState(false);

  const filters = useReviewFilters(reviews, selectedProductId);

  // Seleciona o primeiro produto só uma vez, ao carregar — sem a ref, esse
  // efeito rodava de novo toda vez que selectedProductId virava null (ex:
  // usuário escolhendo "Todos os produtos" no seletor) e forçava de volta
  // pro primeiro produto, deixando essa opção impossível de usar.
  const didAutoSelectProduct = useRef(false);
  useEffect(() => {
    if (!didAutoSelectProduct.current && products.length > 0) {
      didAutoSelectProduct.current = true;
      setSelectedProductId(products[0]._id);
    }
  }, [products]);

  const openEdit = (review) => setEditingReview(review);
  const closeEdit = () => setEditingReview(null);

  const handleSave = async (review) => {
    setSaving(true);
    try {
      await saveReview(review);
      toast.success('Avaliação salva com sucesso.', { autoClose: 4000 });
      closeEdit();
    } catch (err) {
      toast.error('Não foi possível salvar a avaliação.', { autoClose: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (review) => {
    if (!window.confirm(`Excluir a avaliação de "${review.name}"?`)) return;
    try {
      await removeReview(review._id);
      toast.success('Avaliação excluída.', { autoClose: 4000 });
      if (editingReview?._id === review._id) closeEdit();
    } catch (err) {
      toast.error('Não foi possível excluir a avaliação.', { autoClose: 4000 });
    }
  };

  const handleExport = () => {
    if (filters.filteredReviews.length === 0) {
      toast.info('Nenhuma avaliação para exportar com os filtros atuais.', { autoClose: 4000 });
      return;
    }
    exportReviewsToXls(filters.filteredReviews);
  };

  return (
    <div className="cp-page reviews-page">
      <ToastContainer />

      <div className="cp-topbar">
        <div>
          <p className="cp-topbar-breadcrumb">Avaliações</p>
          <h1 className="cp-topbar-title">Avaliações</h1>
          <p className="reviews-page__subtitle">Gerencie as avaliações dos clientes sobre seus produtos.</p>
        </div>
        <div className="cp-topbar-actions">
          <button type="button" className="cp-btn cp-btn--primary" onClick={handleExport}>
            <LuDownload size={16} style={{ marginRight: 6 }} />
            Exportar
          </button>
          <ReviewFiltersPanel
            ratingMin={filters.ratingMin}
            onRatingMinChange={filters.setRatingMin}
            dateStart={filters.dateStart}
            onDateStartChange={filters.setDateStart}
            dateEnd={filters.dateEnd}
            onDateEndChange={filters.setDateEnd}
            onClear={() => {
              filters.setRatingMin(0);
              filters.setDateStart('');
              filters.setDateEnd('');
            }}
          />
        </div>
      </div>

      {error && <div className="reviews-page__error">Não foi possível carregar as avaliações. Tente novamente.</div>}

      <div className="reviews-page__product-field">
        <label>Produto</label>
        <ProductSelect products={products} selectedProductId={selectedProductId} onSelect={setSelectedProductId} />
      </div>

      <ReviewsStatsCards stats={filters.stats} />

      <ReviewsTable
        reviews={filters.pagedReviews}
        counts={filters.counts}
        tab={filters.tab}
        onTabChange={filters.setTab}
        search={filters.search}
        onSearchChange={filters.setSearch}
        onEdit={openEdit}
        onDelete={handleDelete}
        page={filters.page}
        totalPages={filters.totalPages}
        onPageChange={filters.setPage}
      />

      <SidePanel isOpen={Boolean(editingReview)} onClose={closeEdit} title="Editar avaliação">
        {editingReview && (
          <ReviewEditPanel
            review={editingReview}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={closeEdit}
            saving={saving}
          />
        )}
      </SidePanel>

      {loading && <Loading overlay />}
    </div>
  );
};

export default ReviewsPage;
